import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { captureException } from '@/lib/analytics/posthog';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    resetKey: number;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        resetKey: 0,
    };

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        captureException(error, { componentStack: errorInfo.componentStack ?? undefined });
    }

    private handleRetry = () => {
        this.setState((s) => ({
            hasError: false,
            error: null,
            resetKey: s.resetKey + 1,
        }));
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback !== undefined) {
                return this.props.fallback;
            }
            return (
                <div className="p-4 w-full min-h-[40vh] flex items-center justify-center bg-background">
                    <Alert variant="destructive" className="max-w-md w-full">
                        <AlertTitle>Algo deu errado</AlertTitle>
                        <AlertDescription className="flex flex-col items-start gap-2">
                            <span>{this.state.error?.message ?? 'Erro inesperado'}</span>
                            <button
                                type="button"
                                className="text-sm font-medium underline underline-offset-2"
                                onClick={this.handleRetry}
                            >
                                Tentar novamente
                            </button>
                        </AlertDescription>
                    </Alert>
                </div>
            );
        }

        return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }
}
